import React from 'react'
import PropTypes from 'prop-types'
import useEvent from '@react-hook/event'
import { Input, InputGroup, InputLeftElement, InputRightElement, CloseButton } from '@chakra-ui/react'

import { useCombinedRefs } from '../../hooks'

const STYLE = { RIGHT_ELEMENT_WIDTH: '3rem' }
export { STYLE as ENHANCED_INPUT_STYLE }

export const EnhancedInput = React.forwardRef(({ leftElement, rightElement, onChange: onChangeProp, onClear, propsOf = {}, ...rest }, ref) => {
  const inputRef = useCombinedRefs(ref)
  const hasValue = React.useMemo(() => !!rest.value, [rest.value])
  const [isFocused, setIsFocused] = React.useState(false)
  useEvent(inputRef.current, 'focus', () => setIsFocused(true))
  // NOTE: update focus status in microtask
  // to avoid setting focus to true synchronizely before clicking clear button
  useEvent(inputRef.current, 'blur', () => setTimeout(() => setIsFocused(false), 100))

  const onChange = React.useCallback(
    (event) => {
      if (!isFocused) setIsFocused(true)
      onChangeProp && onChangeProp(event)
    },
    [onChangeProp, isFocused],
  )

  const onClearClick = React.useCallback(
    (event) => {
      const simulateEvent = new window.Event('change', { bubbles: true })

      inputRef.current.dispatchEvent(simulateEvent)
      simulateEvent.target.value = ''

      onChange(simulateEvent)
      onClear && onClear(event)
      inputRef.current.focus()
    },
    [inputRef, onChange, onClear],
  )

  return (
    <InputGroup zIndex='0' position='relative' borderRadius='inherit' {...propsOf.container}>
      {leftElement && (
        <InputLeftElement
          pointerEvents='none'
          px='0'
          pt={rest.pt ?? rest.paddingTop} // align to input text
          pb={rest.pb ?? rest.paddingBottom} // align to input text
          {...propsOf.leftElementContainer}
        >
          {leftElement}
        </InputLeftElement>
      )}

      <Input ref={inputRef} overflow='auto' pr={STYLE.RIGHT_ELEMENT_WIDTH} onChange={onChange} {...rest} />

      {(rightElement || !rest.readOnly) && (
        <InputRightElement pointerEvents='none' px='0' width={STYLE.RIGHT_ELEMENT_WIDTH} {...propsOf.rightElementContainer}>
          {rightElement ? (
            React.cloneElement(rightElement, { pointerEvents: 'auto' })
          ) : (
            <CloseButton
              size='sm'
              color='gray.700'
              borderRadius='full'
              onClick={onClearClick}
              pointerEvents='auto'
              opacity={hasValue && isFocused ? 1 : 0}
              visibility={hasValue && isFocused ? 'visible' : 'hidden'}
            />
          )}
        </InputRightElement>
      )}
    </InputGroup>
  )
})

EnhancedInput.propTypes = {
  leftElement: PropTypes.node,
  rightElement: PropTypes.element,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  propsOf: PropTypes.object,
}
